/**
 * Shot put. Alternate the keys to build power in the circle; Space down freezes the power
 * and starts the angle gauge; release puts the shot. The shot then flies as a real
 * projectile from a 2.1 m release height, so the best angle is a little under 45°.
 */
import { createCadence, recordPress, updateCadence, type Cadence, type RunKey } from '../input.js';
import { flightAt, flightLandingTime, type Flight } from '../physics.js';
import { clamp } from '../rng.js';
import { SIM_DT, speedCurve } from './runner.js';

export interface ThrowConfig {
  /** Presses per second for full power. */
  discipline?: 'discus' | 'javelin';
  drag?: number;
  lift?: number;
  sweetCadence: number;
  /** How quickly power follows cadence (1/s). */
  powerRate: number;
  /** Release speed at zero and full power (m/s). */
  vMin: number;
  vMax: number;
  releaseHeight: number;
  gaugeRate: number;
  /** Distance from the athlete's mark to the inside of the stop board (m). */
  boardOffset: number;
  attempts: number;
  /** Mashing this long without releasing counts as stepping out of the circle. */
  maxWindup: number;
}

export const SHOT_PUT: ThrowConfig = {
  sweetCadence: 10,
  powerRate: 1.4,
  vMin: 6.0,
  vMax: 12.8,
  releaseHeight: 2.1,
  gaugeRate: 80,
  boardOffset: 1.0,
  attempts: 3,
  maxWindup: 6,
};

export const DISCUS: ThrowConfig = {
  ...SHOT_PUT,
  discipline: 'discus',
  vMin: 12,
  vMax: 23,
  releaseHeight: 1.6,
  boardOffset: 0,
  maxWindup: 8,
  drag: 0.003,
  lift: 0.055,
};
export const JAVELIN: ThrowConfig = {
  ...SHOT_PUT,
  discipline: 'javelin',
  vMin: 13,
  vMax: 25,
  releaseHeight: 2,
  boardOffset: 30,
  maxWindup: 12,
  drag: 0.0015,
  lift: 0.035,
};
export function idealThrowAngle(cfg: ThrowConfig): number {
  return cfg.discipline === 'javelin' ? 35 : cfg.discipline === 'discus' ? 38 : 41;
}

/** Best release angle for a shot from 2.1 m at these speeds is about 41°. */
export const SP_IDEAL_ANGLE = 41;

export type ThrowPhase = 'power' | 'gauge' | 'flight' | 'landed' | 'foul' | 'done';

export interface ThrowState {
  phase: ThrowPhase;
  runX: number;
  runV: number;
  heading: number;
  vx: number;
  vy: number;
  t: number;
  cadence: Cadence;
  /** 0..1.04: smoothed, follows cadence. */
  power: number;
  gaugeStart: number | null;
  angle: number;
  flight: Flight | null;
  /** Shot position while in flight (x along the throw, y height). */
  shotX: number;
  shotY: number;
  attempt: number;
  results: (number | null)[];
  mark: number | null;
  best: number | null;
  foul: 'stepped' | 'short' | 'sector' | null;
  /** Power at release, for coaching. */
  releasePower: number;
  /** Time of the first counted press this attempt (the wind-up clock starts here). */
  firstPress: number | null;
}

function fresh(): Pick<
  ThrowState,
  | 'runX'
  | 'runV'
  | 'heading'
  | 'vx'
  | 'vy'
  | 'phase'
  | 't'
  | 'cadence'
  | 'power'
  | 'gaugeStart'
  | 'angle'
  | 'flight'
  | 'shotX'
  | 'shotY'
  | 'mark'
  | 'foul'
  | 'releasePower'
  | 'firstPress'
> {
  return {
    runX: 0,
    runV: 0,
    heading: 0,
    vx: 0,
    vy: 0,
    phase: 'power',
    t: 0,
    cadence: createCadence(),
    power: 0,
    gaugeStart: null,
    angle: 0,
    flight: null,
    shotX: 0,
    shotY: 2.1,
    mark: null,
    foul: null,
    releasePower: 0,
    firstPress: null,
  };
}

export function createThrow(): ThrowState {
  return { ...fresh(), attempt: 0, results: [], best: null };
}

export function throwPress(s: ThrowState, key: RunKey, t: number): void {
  if (s.phase !== 'power') return;
  if (recordPress(s.cadence, key, Math.max(0, t)) === 'counted' && s.firstPress === null)
    s.firstPress = Math.max(0, t);
}

/** Space down: freeze power, start the gauge. Space up: put the shot. */
export function throwAction(s: ThrowState, cfg: ThrowConfig, down: boolean, t: number): void {
  if (down && s.phase === 'power') {
    throwAdvance(s, cfg, t);
    if (s.phase !== 'power') return;
    s.phase = 'gauge';
    s.gaugeStart = t;
    s.angle = 0;
  } else if (!down && s.phase === 'gauge') {
    s.t = Math.max(s.t, t);
    s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
    const v = cfg.vMin + (cfg.vMax - cfg.vMin) * clamp(s.power, 0, 1.04);
    const a = (s.angle * Math.PI) / 180;
    s.releasePower = s.power;
    if (cfg.discipline === 'javelin') {
      s.heading = Math.sin(((s.angle - 35) * Math.PI) / 90) * 30;
      if (Math.abs(s.heading) > 14.5) {
        end(s, null, 'sector');
        return;
      }
    }
    if (cfg.discipline === 'discus' && Math.abs(s.heading) > 17.46) {
      end(s, null, 'sector');
      return;
    }
    if (cfg.discipline === 'javelin' && s.runX > cfg.boardOffset) {
      end(s, null, 'stepped');
      return;
    }
    s.flight = {
      x0: cfg.discipline === 'javelin' ? s.runX : 0,
      y0: cfg.releaseHeight,
      t0: s.t,
      vx: v * Math.cos(a),
      vy: v * Math.sin(a),
    };
    s.shotX = s.flight.x0;
    s.shotY = cfg.releaseHeight;
    s.vx = s.flight.vx;
    s.vy = s.flight.vy;
    s.phase = 'flight';
  }
}

function end(s: ThrowState, mark: number | null, foul: ThrowState['foul']): void {
  s.mark = mark;
  s.foul = foul;
  s.results.push(mark);
  if (mark !== null && (s.best === null || mark > s.best)) s.best = mark;
  s.phase = mark === null ? 'foul' : 'landed';
}

export function throwAdvance(s: ThrowState, cfg: ThrowConfig, until: number): void {
  while (
    s.t + SIM_DT <= until + 1e-9 &&
    (s.phase === 'power' || s.phase === 'gauge' || s.phase === 'flight')
  ) {
    s.t += SIM_DT;
    if (cfg.discipline === 'javelin' && (s.phase === 'power' || s.phase === 'gauge')) {
      s.runV += (s.power * 8.5 - s.runV) * 0.04;
      s.runX += s.runV * SIM_DT;
      if (s.runX > cfg.boardOffset) {
        end(s, null, 'stepped');
        continue;
      }
    }
    if (
      cfg.discipline === 'discus' &&
      s.firstPress !== null &&
      (s.phase === 'power' || s.phase === 'gauge')
    )
      s.heading = Math.sin(((s.t - s.firstPress) * Math.PI * 2) / 1.6) * 28;
    if (s.phase === 'power') {
      const cad = updateCadence(s.cadence, s.t, SIM_DT);
      const target = speedCurve(cad / cfg.sweetCadence);
      s.power += (target - s.power) * (1 - Math.exp(-SIM_DT * cfg.powerRate));
      // Winding up forever: you have stepped out of the circle.
      if (s.firstPress !== null && s.t - s.firstPress > cfg.maxWindup) end(s, null, 'stepped');
    } else if (s.phase === 'gauge') {
      s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
      if (s.angle >= 90 && s.t - (s.gaugeStart ?? s.t) > 90 / cfg.gaugeRate + 1.5) {
        // Holding the shot at the top forever: dropped it in the circle.
        end(s, null, 'short');
      }
    } else if (s.phase === 'flight' && s.flight) {
      if (cfg.discipline) {
        const speed = Math.hypot(s.vx, s.vy);
        s.vx -= (cfg.drag ?? 0) * speed * s.vx * SIM_DT;
        s.vy += (-9.81 - (cfg.drag ?? 0) * speed * s.vy + (cfg.lift ?? 0) * s.vx) * SIM_DT;
        s.shotX += s.vx * SIM_DT;
        s.shotY += s.vy * SIM_DT;
        if (s.shotY <= 0) {
          s.shotY = 0;
          const d = Math.round((s.shotX - cfg.boardOffset) * 100) / 100;
          end(s, d > 0 ? d : null, d > 0 ? null : 'short');
        }
        continue;
      }
      const p = flightAt(s.flight, s.t);
      s.shotX = p.x;
      s.shotY = Math.max(0, p.y);
      const landT = s.flight.t0 + flightLandingTime(s.flight, 0);
      if (s.t >= landT) {
        const land = flightAt(s.flight, landT);
        s.shotX = land.x;
        s.shotY = 0;
        const dist = Math.round((land.x - cfg.boardOffset) * 100) / 100;
        if (dist <= 0) end(s, null, 'short');
        else end(s, dist, null);
      }
    }
  }
}

export function throwTimeout(s: ThrowState): void {
  if (s.phase !== 'power') return;
  end(s, null, 'stepped');
}

export function throwNext(s: ThrowState, cfg: ThrowConfig): void {
  s.attempt++;
  if (s.attempt >= cfg.attempts) {
    s.phase = 'done';
    return;
  }
  Object.assign(s, fresh());
}

/** Range for a given power and angle, for tests and coaching. */
export function throwRange(cfg: ThrowConfig, power: number, angleDeg: number): number {
  const v = cfg.vMin + (cfg.vMax - cfg.vMin) * clamp(power, 0, 1.04);
  const a = (angleDeg * Math.PI) / 180;
  const f: Flight = {
    x0: 0,
    y0: cfg.releaseHeight,
    t0: 0,
    vx: v * Math.cos(a),
    vy: v * Math.sin(a),
  };
  return f.vx * flightLandingTime(f, 0) - cfg.boardOffset;
}
